import { FieldInfo } from '@/components/FieldInfo';
import { useCreateOrganization } from '@/config/organizations';
import { BasicInfoSection } from '@/pages/Organizations/components/BasicInfoSection';
import { gridProps, stackProps } from '@/pages/Organizations/components/constants';
import { ContactFields } from '@/pages/Organizations/components/ContactFields';
import { CreateOrgActions } from '@/pages/Organizations/components/CreateOrgActions';
import { SubscriptionSection } from '@/pages/Organizations/components/SubscriptionSection';
import { FullLoading } from '@components/index';
import { Grid } from '@nautilusbio/nb-design-system/Grid';
import { Input } from '@nautilusbio/nb-design-system/Input';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { useStore } from '@tanstack/react-form';
import { useNavigate, createLazyFileRoute } from '@tanstack/react-router';
import React from 'react';
import { useTranslation } from 'react-i18next';

import orgStyles from '../../pages/Organizations/styles.module.css';

export const Route = createLazyFileRoute('/organizations/new')({
  component: CreateOrganizationPage,
  pendingComponent: () => {
    const { t } = useTranslation('orgs');
    return <FullLoading addEllipsis label={t('fetching')} />;
  },
});

function CreateOrganizationPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('orgs');
  const { form } = useCreateOrganization();

  const handleCancel = () => {
    form.reset();
    // If browser history exists, go back; otherwise, navigate to the organizations listing
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate({ to: '/organizations' });
    }
  };

  const handleSubmit = form.handleSubmit;

  const formCanSubmit = useStore(
    form.store,
    s => s.canSubmit && !!s.fieldMeta.name?.isDirty && !!s.fieldMeta.domain?.isDirty,
  );

  const isSubmitting = useStore(form.store, s => s.isSubmitting);

  return (
    <Stack {...stackProps} fullHeight gap={4}>
      <CreateOrgActions
        onCancel={handleCancel}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        canSubmit={formCanSubmit && !isSubmitting}
        name={form.state.values.name}
      />

      <div className={orgStyles.formContainer}>
        <form
          className={orgStyles.form}
          onSubmit={e => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <BasicInfoSection form={form} />

          <Grid {...gridProps}>
            <ContactFields form={form} section="primaryContact" />
            <ContactFields form={form} section="itContact" />
          </Grid>

          <Grid {...gridProps} alignItems="end">
            <Stack {...stackProps} alignItems="start">
              <Text as="h3" className={orgStyles.sectionHeading}>
                {t('create_org_section_domain')}
              </Text>

              <form.Field name="domain">
                {field => (
                  <>
                    <Input
                      type="text"
                      className={orgStyles.uiInput}
                      data-testid="input-domain"
                      id="domain"
                      name="domain"
                      onChange={e => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      placeholder={t('create_org_domain_placeholder')}
                      value={field.state.value}
                    />
                    <FieldInfo field={field} />
                  </>
                )}
              </form.Field>
            </Stack>

            <SubscriptionSection form={form} />
          </Grid>
        </form>

        {isSubmitting && <FullLoading label={t('create_org_creating', { name: form.state.values.name })} />}
      </div>
    </Stack>
  );
}
